import React from 'react';
import type { ReactNode } from 'react';
import { RelayEnvironmentProvider, useLazyLoadQuery } from 'react-relay';

import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';
import { SpaceDetailProvider } from '@confluence/space-utils/entry-points/SpaceDetailProvider.ts';

import { ContainerSkeleton } from './ContainerSkeleton.tsx';
import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery,
	type SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';

type Props = {
	children: ReactNode;
	variables: SpaceNavigationQuery$variables;
	loading: boolean;
	hasError: boolean;
};

const SharedSpaceData = ({ children, variables, loading, hasError }: Props) => {
	// Store-only never starts a request, but can suspend on an in-flight navigation request.
	const data = useLazyLoadQuery<SpaceNavigationQuery>(SpaceNavigationQueryArtifact, variables, {
		fetchPolicy: 'store-only',
	});
	return (
		<SpaceDetailProvider
			spaceKey={variables.spaceKey}
			loading={loading && !hasError}
			spaceFragmentRef={!loading && !hasError ? (data?.space?.sharedSpaceDetail ?? null) : null}
		>
			{children}
		</SpaceDetailProvider>
	);
};

export const SpaceNavigationSharedSpaceData = (props: Props): React.JSX.Element => (
	<RelayEnvironmentProvider environment={getRelayEnvironment()}>
		<RelaySuspense
			interactionName="SpaceNavigationSharedSpaceData"
			fallback={<ContainerSkeleton loader />}
		>
			<SharedSpaceData {...props} />
		</RelaySuspense>
	</RelayEnvironmentProvider>
);
